import React, { useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { InfoCard, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useAsync } from 'react-use';

type Metric = {
  label: string;
  value: string;
  unit: string;
};

type MetricsResponse = {
  status: string;
  statusColor: 'green' | 'yellow' | 'red' | 'grey';
  metrics: Metric[];
  consoleUrl: string;
};

const STATUS_COLORS: Record<string, string> = {
  green: '#2e7d32',
  yellow: '#f57f17',
  red: '#c62828',
  grey: '#757575',
};

const useStyles = makeStyles(theme => ({
  metricTile: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    height: '100%',
  },
  metricLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  metricValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  metricUnit: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  statusChip: {
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  footer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  unsupported: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

function MetricTile({ label, value, unit }: Metric) {
  const classes = useStyles();
  return (
    <Box className={classes.metricTile}>
      <Typography className={classes.metricLabel}>{label}</Typography>
      <Box display="flex" alignItems="baseline">
        <Typography className={classes.metricValue}>{value}</Typography>
        {unit && (
          <Typography component="span" className={classes.metricUnit}>
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const SUPPORTED_TYPES = ['database', 'cache', 'storage'];

export function EntityAwsResourceCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [refreshCount, setRefreshCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const entityName = entity.metadata.name;
  const resourceType = entity.spec?.type as string | undefined;
  const lifecycle = entity.spec?.lifecycle as string | undefined;
  const clusterId = entity.metadata.annotations?.['aws/elasticache-cluster-id'];
  const accountId = entity.metadata.annotations?.['aws/account-id'];

  const { value, loading, error } = useAsync(async () => {
    if (!resourceType || !SUPPORTED_TYPES.includes(resourceType)) return null;

    const baseUrl = await discoveryApi.getBaseUrl('aws-cloudwatch');
    const params = new URLSearchParams({ entityName, resourceType });
    if (lifecycle) params.set('lifecycle', lifecycle);
    if (clusterId) params.set('clusterId', clusterId);
    if (accountId) params.set('accountId', accountId);

    const resp = await fetchApi.fetch(`${baseUrl}/metrics?${params}`);
    if (!resp.ok) {
      const body = await resp.text();
      throw new Error(`AWS Monitoring API error ${resp.status}: ${body}`);
    }
    const data: MetricsResponse = await resp.json();
    setLastUpdated(new Date());
    return data;
  }, [entityName, resourceType, lifecycle, clusterId, accountId, refreshCount]);

  if (!resourceType || !SUPPORTED_TYPES.includes(resourceType)) {
    return (
      <InfoCard title="AWS Monitoring">
        <Box className={classes.unsupported}>
          <Typography variant="body2">
            No AWS monitoring available for resource type{' '}
            <strong>{resourceType ?? 'unknown'}</strong>.
          </Typography>
          <Typography variant="caption">
            Supported types: {SUPPORTED_TYPES.join(', ')}
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  const cardAction = (
    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
      {value && (
        <Chip
          size="small"
          label={value.status}
          className={classes.statusChip}
          style={{
            backgroundColor: STATUS_COLORS[value.statusColor] ?? STATUS_COLORS.grey,
            color: '#fff',
          }}
        />
      )}
      <Tooltip title="Refresh">
        <IconButton
          size="small"
          onClick={() => setRefreshCount(c => c + 1)}
          disabled={loading}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {value?.consoleUrl && (
        <Tooltip title="Open in AWS Console">
          <IconButton
            size="small"
            component="a"
            href={value.consoleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <InfoCard title="AWS Monitoring" action={cardAction}>
      {loading && <Progress />}
      {error && <ResponseErrorPanel error={error} />}
      {!loading && !error && value && (
        <>
          <Grid container spacing={2}>
            {value.metrics.map(m => (
              <Grid item xs={6} md={4} key={m.label}>
                <MetricTile {...m} />
              </Grid>
            ))}
          </Grid>
          <Box className={classes.footer}>
            <Typography className={classes.footerText}>
              {lastUpdated ? `Updated ${formatRelative(lastUpdated)}` : ''}
            </Typography>
          </Box>
        </>
      )}
    </InfoCard>
  );
}

function formatRelative(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}
